import React, { useState, useEffect, useRef } from 'react';
import appConfig from './config/appConfig';

const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  }
};

const AddApproverDialog = ({
  isOpen,
  onClose,
  selectedRequirements,
  selectedLevelId,
  corporateAccount,
  currentProject,
  directLinkPath,
  login_token,
  functionalLevelDescription,
  requirements,
  req_type
}) => {
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const userSearchRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchAvailableUsers();
      setSelectedUser('');
      setUserSearchTerm('');
      setError('');
      setSuccessCount(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && userSearchRef.current) {
      userSearchRef.current.focus();
    }
  }, [isOpen]);

  const fetchAvailableUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_user_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setAvailableUsers(data.user_list || []);
      } else {
        throw new Error(data.status_description || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load available users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const generateRequirementDirectLink = (reqId, levelId, projectId) => {
    const baseUrl = window.location.origin;
    if (reqId) {
      return `${baseUrl}${directLinkPath}?project=${encodeURIComponent(projectId)}&reqId=${encodeURIComponent(reqId)}&action=edit`;
    } else if (levelId) {
      return `${baseUrl}${directLinkPath}?project=${encodeURIComponent(projectId)}&levelId=${encodeURIComponent(levelId)}`;
    } else {
      return `${baseUrl}${directLinkPath}?project=${encodeURIComponent(projectId)}`;
    }
  };

  const handleAddApprover = async () => {
    setIsLoading(true);
    setError('');
    setSuccessCount(0);

    if (!selectedUser) {
      setError('Please select a user');
      setIsLoading(false);
      return;
    }

    let levelId = null;
    let reqIds = [];

    if (req_type === 'FUNCTIONAL LEVEL') {
      if (!selectedLevelId) {
        setError('Please select a functional level');
        setIsLoading(false);
        return;
      }
      levelId = selectedLevelId;
    } else if (req_type === 'REQUIREMENT' || req_type === 'INTEGRATION REQUIREMENT') {
      if (selectedRequirements.size === 0) {
        setError('Please select at least one requirement');
        setIsLoading(false);
        return;
      }
      reqIds = Array.from(selectedRequirements);
    }

    try {
      let itemDirectLink = '';
      if (reqIds.length > 0) {
        const firstReqId = reqIds[0];
        const requirement = requirements.find(r => r.req_id === firstReqId);
        if (requirement) {
          itemDirectLink = generateRequirementDirectLink(requirement.req_id_with_prefix, null, currentProject);
        }
      } else {
        itemDirectLink = generateRequirementDirectLink(null, levelId, currentProject);
      }

      const response = await fetch(appConfig.getUrl('/add_requirement_approver'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          level_id: levelId || '',
          req_ids: reqIds.length > 0 ? reqIds : [],
          approval_user_id: selectedUser,
          item_direct_link: itemDirectLink
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const count = data.success_count || reqIds.length || 1;
        setSuccessCount(count);
        setError(`Successfully added approver to ${count} requirement${count !== 1 ? 's' : ''}`);

        setTimeout(() => {
          onClose(true);
        }, 2000);
      } else {
        throw new Error(data.status_description || 'Failed to add approver');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error adding approver:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const getLevelTitle = () => {
    if (req_type === 'PROJECT LEVEL') {
      return 'Project Level (Overall project level approvers)';
    } else if (req_type === 'FUNCTIONAL LEVEL') {
      return `Functional Level: ${functionalLevelDescription || 'Select a functional level'}`;
    } else {
      return `${selectedRequirements.size} Requirement${selectedRequirements.size !== 1 ? 's' : ''} Selected`;
    }
  };

  const getSelectedRequirementDetails = () => {
    if (selectedRequirements.size === 0) return [];
    return Array.from(selectedRequirements).map(reqId => {
      const req = requirements.find(r => r.req_id === reqId);
      return req ? {
        id: req.req_id,
        idWithPrefix: req.req_id_with_prefix,
        description: req.req_description
      } : null;
    }).filter(Boolean);
  };

  return (
    <div style={DialogStyles.overlay}>
      <div className="bg-white rounded-lg max-w-screen-xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <h1 className="text-l font-bold text-black bg-[#CCCCFF] text-center flex items-center justify-center rounded-t-lg" style={{ height: '3em' }}>
          Add Approver
        </h1>

        <div className="p-6 overflow-y-auto">
          <div className="mb-6 p-4 border border-gray-300 rounded-md">
            <h3 className="text-base font-semibold mb-3">Adding approver to:</h3>
            <p>{getLevelTitle()}</p>
            
            {selectedRequirements.size > 0 && (
              <div className="mt-4 max-h-40 overflow-y-auto">
                <p className="text-sm font-medium mb-2">Selected requirements:</p>
                <div className="space-y-1">
                  {getSelectedRequirementDetails().slice(0, 10).map(req => (
                    <div key={req.id} className="text-xs text-gray-700 bg-gray-50 p-2 rounded">
                      <span className="font-medium">{req.idWithPrefix}</span>: {req.description.substring(0, 100)}...
                    </div>
                  ))}
                  {selectedRequirements.size > 10 && (
                    <div className="text-xs text-gray-500 italic">
                      ... and {selectedRequirements.size - 10} more
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Select User: <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Start typing to search users..."
                value={userSearchTerm}
                ref={userSearchRef}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                className="w-full p-2 border rounded mb-1"
                disabled={isLoadingUsers || isLoading}
              />

              {isLoadingUsers ? (
                <p className="text-sm text-gray-500 mt-1">Loading users...</p>
              ) : (
                <div className="max-h-60 overflow-y-auto border rounded">
                  {availableUsers
                    .filter(user =>
                      userSearchTerm === '' ||
                      user.user_name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                      user.user_id.toLowerCase().includes(userSearchTerm.toLowerCase())
                    )
                    .map(user => (
                      <div
                        key={user.user_id}
                        className={`p-2 cursor-pointer hover:bg-blue-50 ${selectedUser === user.user_id ? 'bg-blue-100' : ''}`}
                        onClick={() => {
                          setSelectedUser(user.user_id);
                          setUserSearchTerm(`${user.user_name} (${user.user_id})`);
                        }}
                      >
                        {user.user_name} ({user.user_id})
                      </div>
                    ))}

                  {userSearchTerm === '' && (
                    <div className="p-2 text-gray-500">Type to search for users</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className={`mb-4 p-2 rounded ${
              error.includes('Successfully')
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-500'
            }`}>
              {error}
            </div>
          )}

          <div className="flex justify-center gap-4">
            <button
              type="button"
              onClick={() => {
                onClose(false);
                setSelectedUser('');
                setUserSearchTerm('');
                setError('');
                setSuccessCount(0);
              }}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              disabled={isLoading}
            >
              Close
            </button>

            <button
              type="button"
              onClick={handleAddApprover}
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                opacity: isLoading ? 0.5 : 1
              }}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : `Add Approver${selectedRequirements.size > 1 ? ` to ${selectedRequirements.size} Requirements` : ''}`}
            </button>
          </div>

          {!isLoading && successCount === 0 && (
            <p className="text-gray-600 text-sm mt-4 text-center">
              This will add the selected user as an approver to {selectedRequirements.size > 0 ? `${selectedRequirements.size} requirement${selectedRequirements.size !== 1 ? 's' : ''}` : 'the selected level'}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddApproverDialog;